/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IActionContext } from '@msdyn365-commerce/core';
import { ICartViewProps } from './krannich-c-cart';
import { ICartlinesViewProps } from './components/krannich-c-cart-line-items';
import { ICartLineItemViewProps } from './components/krannich-c-cart-line-items-promotion';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Node, Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getCartState, getCheckoutState } from '@msdyn365-commerce/global-state';
import { IKrannichCCartResources } from './krannich-c-cart.props.autogenerated';
import { currencyFormat, dateFormatByLocale, incExcPriceFormat, taxPercentage } from '../../utils/helper';
import { IKRV_GetATPLeadTimeResponseQuantity } from '../../actions/customProxy/ATP1/DataServiceEntities.g';
import { KitProduct } from '../../utils/Kits/kits';

/* VSI CUSTOMIZATION | DATE FORMAT | START */
const getCartLineShipDate = (shipDate: IKRV_GetATPLeadTimeResponseQuantity, itemId?: string, locale?: string, currency?: string) => {
    let date = shipDate?.ATPLeadTimePropertiesQuantityResponseEntityList?.find(e => e.ItemId === itemId)?.ATPDate;
    if (locale && currency && date) {
        date = dateFormatByLocale(locale, currency, date);
    }
    return <label>{date}</label>;
};
/* VSI CUSTOMIZATION | DATE FORMAT | END */
const _renderCartlines = (
    cartLines: ICartlinesViewProps[] | undefined,
    cartEmptyText: string,
    CartlinesWrapper: INodeProps,
    storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    cartDataResult: boolean,
    resources: IKrannichCCartResources,
    shipDate: IKRV_GetATPLeadTimeResponseQuantity,
    props: ICartViewProps
): JSX.Element[] | JSX.Element => {
    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    const { enabledIncExPrices } = props.context.app.config;
    if (cartLines) {
        return cartLines.map((cartLine: ICartlinesViewProps, index: number) => {
            const atpErrorMsg = props.atpCartlineResults?.find(e => e?.ItemId?.toString() === cartLine?.data?.cartline?.ItemId)
                ?.ErrorMessage;
            const linkedProducts: any[] | undefined = cartLine?.data?.product?.Components;
            const unitPrice = cartLine.data?.cartline.ExtendedPrice
                ? cartLine.data?.cartline.ExtendedPrice / cartLine.data?.cartline.Quantity!
                : cartLine.data?.cartline.Price;
            const customerTaxPercentage = taxPercentage(props.data.customerInformation?.result!);
            return (
                <div className='msc-cart-lines-item' key={`${cartLine.cartlineId ?? ''}-${MsDyn365.isBrowser.toString()}`}>
                    {cartLine.cartline}

                    <div className='cart-unit-price'>
                        <span className='unit-price-title'>{resources.productUnitPrice}</span>
                        <span className='unit-price-amount'>
                            {unitPrice === 0 ? (
                                <div className='no-price-wrapper'>
                                    <span className='label'>{resources.requestPriceText}</span>
                                    <div className='tooltip-wrapper' tabIndex={0}>
                                        <span className='tooltip-icon' />
                                        <span className='tooltip-text tooltip-left'>{resources.zeroPriceProductText}</span>
                                    </div>
                                </div>
                            ) : (
                                incExcPriceFormat(unitPrice!, props.context, enabledIncExPrices, customerTaxPercentage)
                            )}
                        </span>
                    </div>
                    <div className='cart-Item-id'>
                        <span className='item-number-title'>{resources.productItemNumber}</span>
                        <span className='item-number'>{cartLine.data?.product?.ItemId}</span>
                    </div>
                    {/* VSI CUSTOMIZATION START ATP  */}
                    <div className='form-field ship-date'>
                        {shipDate && (
                            <>
                                <label>{resources.shipDateText}</label>
                                {/* VSI CUSTOMIZATION | DATE FORMAT | START */}
                                {getCartLineShipDate(
                                    shipDate,
                                    cartLine.data?.product?.ItemId,
                                    props.context.app.config.dateFormat,
                                    props.context.request.channel?.Currency
                                )}
                                {/* VSI CUSTOMIZATION | DATE FORMAT | END */}
                            </>
                        )}
                    </div>
                    <div className='cart-Item-id error'>{atpErrorMsg?.length > 0 && { atpErrorMsg }}</div>
                    {linkedProducts?.length !== 0 ? <KitProduct linkedProducts={linkedProducts} props={props} /> : null}
                    {/* VSI CUSTOMIZATION END ATP  */}
                    {storeSelector && cartLine.pickUpInStore ? (
                        <Node {...cartLine.pickUpInStore.ContainerProps}>{cartLine.pickUpInStore.defaultComponent}</Node>
                    ) : null}
                    <div className='ot-btn-wrapper'>
                        {cartLine.addToOrderTemplate ? (
                            <Node className='msc-cart-line__extra-actions'>
                                {cartLine.remove}
                                {/* {cartLine.addToWishlist} */}
                                {cartLine.addToOrderTemplate}
                            </Node>
                        ) : (
                            <>
                                {cartLine.remove}
                                {/* {cartLine.addToWishlist} */}
                            </>
                        )}
                    </div>
                </div>
            );
        });
    }
    return cartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <p className='msc-cart-line'>{cartEmptyText}</p>
            {backToShoppingButton}
        </div>
    ) : (
        <>{waitingComponent}</>
    );
};

/**
 * Method will render error block.
 * @param errorData -The order summary errors interface.
 * @returns Jsx element.
 */
// const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
//     if (!errorData || !ArrayExtensions.hasElements(errorData.errors)) {
//         return null;
//     }
//     return (
//         <Node {...errorData.Wrapper}>
//             {errorData.header}
//             {errorData.errors}
//         </Node>
//     );
// };

/**
 * Method will render invoice summary lines.
 * @param invoiceSummaryLines -The invoicesummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Method will render order summary lines.
 * @param orderSummaryLines -The ordersummary lines interface.
 * @param OrderSummaryItems -The order summary items props.
 * @param props -The cartview props.
 * @returns Jsx element.
 */
const _renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    const {
        config: { lessOrderSummaryCartInfoMessage, lessOrderSummaryCartInfoMessageMX },
        data: {
            cart: { result: cartData }
        },
        context,
        resources
    } = props;
    if (!orderSummaryLines) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }
    const subTotal = cartData?.cart.SubtotalAmount;
    const usSiteFlag = props?.context?.actionContext?.requestContext?.apiSettings?.oun === props.context.app.config.ounNumberForUsSite;
    const mexicoSiteFlag =
        props?.context?.actionContext?.requestContext?.apiSettings?.oun === props.context.app.config.ounNumberForMexicoSite;
    return (
        <Node {...OrderSummaryItems}>
            {props.promoCode}
            {/* {orderSummaryLines.subtotal} */}
            {mexicoSiteFlag}
            {!mexicoSiteFlag && subTotal && subTotal < 1000
                ? lessOrderSummaryCartInfoMessage &&
                  !usSiteFlag && (
                      <div className='extra-charges-message-title'>
                          <span className='extra-charges-message'>{lessOrderSummaryCartInfoMessage}</span>
                      </div>
                  )
                : mexicoSiteFlag &&
                  subTotal &&
                  subTotal < 500 &&
                  lessOrderSummaryCartInfoMessageMX && (
                      <div className='extra-charges-message-title'>
                          <span className='extra-charges-message'>{lessOrderSummaryCartInfoMessageMX}</span>
                      </div>
                  )}
            {
                <p className='msc-order-summary__line-sub-total'>
                    <span className='msc-order-summary__label'>{resources.subtotalLabel}</span>
                    <span className='msc-price msc-order-summary__value'>
                        <span className='msc-price__actual'>{currencyFormat(subTotal, context)}</span>
                        {usSiteFlag && props.config.subTotalToolTipMessageForUsSite && (
                            <div className='tooltip-wrapper' tabIndex={0}>
                                <span className='tooltip-icon' />
                                <span className='tooltip-text tooltip-left'>{props.config.subTotalToolTipMessageForUsSite}</span>
                            </div>
                        )}
                    </span>
                </p>
            }
            {/* {orderSummaryLines.subtotal} */}
            {orderSummaryLines.shipping}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
            <p className='msc-order-summary__line-tax-amount'>
                <span className='msc-order-summary__label'>Total</span>
                <span className='msc-order-summary__value'>
                    <span className='msc-price__actual' itemProp='price'>
                        {currencyFormat(subTotal, context)}
                    </span>
                </span>
            </p>
        </Node>
    );
};

/**
 * Method will render cart promotions.
 * @param promotions -The cartline item view props.
 * @returns Jsx element.
 */
const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

/**
 * Method will render cartlines group title.
 * @param cartLine -The cartline view props.
 * @param titleSeparate -The title separate.
 * @returns Jsx element.
 */
// const renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, titleSeparate: string) => {
//     const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
//     return (
//         <>
//             <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
//                 {isBopisSelected ? (
//                     <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>
//                         {cartLine.pickUpInStore?.deliveryOption}
//                         {titleSeparate}
//                         {cartLine.pickUpInStore?.orgUnitName}
//                     </p>
//                 ) : null}
//             </Node>
//         </>
//     );
// };

/**
 * Method will render cartlines groups header title.
 * @param pickUpText -The pickup text for group.
 * @param shippingText -The shipping text for group.
 * @param emailShippingText -The email shipping text for group.
 * @param titleSeparate -The title separator.
 * @param cartLine -The cartline view props.
 * @param emailDeliveryModeCode -The email delivery code from channel.
 * @returns Jsx element.
 */
const renderCartLinesGroupHeader = (
    pickUpText: string,
    shippingText: string,
    emailShippingText: string,
    titleSeparate: string,
    cartLine: ICartlinesViewProps,
    emailDeliveryModeCode?: string
) => {
    const isBopisSelected: boolean | undefined = cartLine.pickUpInStore?.isBopisSelected;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    if (!StringExtensions.isNullOrEmpty(emailDeliveryModeCode)) {
        groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailShippingText : groupTitle;
    }

    return (
        <>
            <div></div>
            {/* <div className='msc-cart-lines-group-wraper__bopis-heading'>
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}-icon`} />
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}`}>{groupTitle}</p>
                {renderCartLinesGroupTitle(cartLine, titleSeparate)}
            </div> */}
        </>
    );
};

/**
 * Method will render cartlines in groups.
 * @param props -The cartline.
 * @returns Jsx element.
 */
const renderCartLinesGroup = (props: ICartViewProps): JSX.Element | undefined => {
    if (props.cartLinesGroup && ArrayExtensions.hasElements(props.cartLinesGroup)) {
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup.map(cartlines => {
                    return (
                        <div key={cartlines[0].cartlineId} className='msc-cart-lines-group-wraper'>
                            {renderCartLinesGroupHeader(
                                props.resources.pickUpText,
                                props.resources.shippingText,
                                props.resources.emailshippingText,
                                props.resources.titleSeparate,
                                cartlines[0],
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                            )}
                            {
                                <>
                                    <div className='cartline-items-custom-div'>
                                        {_renderCartlines(
                                            cartlines,
                                            props.resources.emptyCartText,
                                            props.CartlinesWrapper,
                                            props.storeSelector,
                                            props.backToShoppingButton,
                                            props.waitingComponent,
                                            props.cartLoadingStatus,
                                            props.cartDataResult,
                                            props.resources,
                                            props.shipDate,
                                            props
                                        )}
                                    </div>
                                    <div className='btn_cart-bottom'>
                                        {' '}
                                        {props.cartDataResult ? null : props.backToShoppingButton} {props.createTemplateFromCartButton}
                                        {!props.cartDataResult && props.requestDate}
                                    </div>
                                </>
                            }
                        </div>
                    );
                })}
            </div>
        );
    }
    return props.cartDataResult ? (
        <div className='msc-cartline-wraper'>
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>{props.resources.emptyCartText}</p>
                {props.backToShoppingButton}
            </div>
        </div>
    ) : (
        <>{props.waitingComponent}</>
    );
};
const _renderCheckoutButton = (error: number, checkoutButton: React.ReactNode, buttonLabel: string, isCheckoutBtnLoading: boolean) => {
    if (error || isCheckoutBtnLoading) {
        return (
            <button
                type='button'
                title='Checkout'
                data-exp-event-id='CartPage.Krannich c cart.Checkout.click'
                className={`${isCheckoutBtnLoading ? 'is-busy' : ''} msc-cart__btn-checkout msc-btn`}
                disabled={true}
            >
                {buttonLabel}
            </button>
        );
    } else {
        return checkoutButton;
    }
};
const CartView: React.FC<ICartViewProps> = (props: ICartViewProps) => {
    const [showAlert, setShowAlert] = React.useState<boolean>(false);
    const toggleAlert = () => {
        setShowAlert(!showAlert);
    };
    async function emptyActiveCart(ctx: IActionContext): Promise<void> {
        let cartState;
        let checkoutState;
        const lineItemIdsToRemove: string[] | undefined = [];

        try {
            cartState = await getCartState(ctx);
            checkoutState = await getCheckoutState(ctx);
        } catch (error) {
            throw error;
        }

        if (!cartState || !cartState.cart) {
            throw new Error('Fail to fetch active cart');
        }

        if (!checkoutState || !checkoutState.checkoutCart) {
            throw new Error('Fail to fetch checkoutCart cart');
        }

        const activeCart = cartState.cart;
        // const checkoutCart = checkoutState.checkoutCart.cart;
        activeCart.CartLines?.map(e => {
            lineItemIdsToRemove.push(e?.LineId!);
        });
        // for (const activeCartLine of activeCart.CartLines || []) {
        //     for (const checkoutCartLine of checkoutCart.CartLines || []) {
        //         if (activeCartLine.LineId && activeCartLine.LineId === checkoutCartLine.LineId) {
        //             lineItemIdsToRemove.push(activeCartLine.LineId);
        //         }
        //     }
        // }

        try {
            await cartState.removeCartLines({ cartLineIds: lineItemIdsToRemove });
            await cartState.removeAllPromoCodes({});
            await checkoutState.removeCheckoutCartId({});
        } catch (error) {
            throw error;
        }
        toggleAlert();
    }

    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            {props.title}
            {props.cartlines && props.cartlines?.length > 0 && (
                <div className='clear-cart'>
                    <button onClick={toggleAlert}>{props.resources.removeAllProductsButton}</button>
                </div>
            )}
            {showAlert && (
                <Modal
                    wrapClassName='ms-order-template-mobile-view-modal buybox-description-popup-container'
                    autoFocus
                    fade={false}
                    applicationNode='renderPage'
                    isOpen={true}
                    toggle={toggleAlert}
                >
                    <div className='alert-modal-body-container'>
                        <ModalHeader toggle={toggleAlert}>{props.resources.alertLabel}</ModalHeader>
                        <ModalBody>
                            <span>{props.resources.removeAllProductsDialogueBox}</span>
                            <div className='alert-btn-group'>
                                <button
                                    className='msc-promo-code__apply-btn btn msc-btn empty-btn'
                                    // eslint-disable-next-line no-return-await
                                    onClick={async () => await emptyActiveCart(props.context.actionContext)}
                                >
                                    {props.resources.removeAllProductsConfirmButton}
                                </button>
                                <button className='msc-promo-code__apply-btn btn msc-btn cancel-btn ' onClick={toggleAlert}>
                                    {props.resources.removeAllProductsCancelButton}
                                </button>
                            </div>
                        </ModalBody>
                    </div>
                </Modal>
            )}
            {props.showAtpStockAlert && (
                <Modal
                    wrapClassName='ms-order-template-mobile-view-modal buybox-description-popup-container'
                    autoFocus
                    fade={false}
                    applicationNode='renderPage'
                    isOpen={props.showAtpStockAlert}
                >
                    <div className='alert-modal-body-container'>
                        <ModalHeader>{props.resources.alertLabel}</ModalHeader>
                        <ModalBody>
                            <span>{props.resources.atpQuantityErrorGeneralMsg}</span>
                        </ModalBody>
                    </div>
                </Modal>
            )}

            {!props.cart?.hasInvoiceLine && props.promotionOptions && _renderPromotions(props.promotionOptions)}
            {props.multiplePickUpEnabled ? (
                renderCartLinesGroup(props)
            ) : (
                <Node {...props.CartlinesWrapper}>
                    <div className='cartline-items-custom-div'>
                        {_renderCartlines(
                            props.cartlines,
                            props.resources.emptyCartText,
                            props.CartlinesWrapper,
                            props.storeSelector,
                            props.backToShoppingButton,
                            props.waitingComponent,
                            props.cartLoadingStatus,
                            props.cartDataResult,
                            props.resources,
                            props.shipDate,
                            props
                        )}
                    </div>

                    <div className='btn_cart-bottom'>
                        {' '}
                        {props.cartDataResult ? null : props.backToShoppingButton} {props.createTemplateFromCartButton}
                        {!props.cartDataResult && props.requestDate}
                    </div>
                </Node>
            )}
            {props.orderSummaryHeading && (
                <Node {...props.OrderSummaryWrapper}>
                    <div className='order-summary-custom-div'>
                        {props.orderSummaryHeading}
                        {props.cart?.hasInvoiceLine
                            ? _renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                            : _renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                        {/* {_renderErrorBlock(props.OrderSummaryErrors)} */}
                        <div className='sticky-button'>
                            {props.OrderSummaryErrors &&
                                _renderCheckoutButton(
                                    props.OrderSummaryErrors?.errors.length,
                                    props.checkoutAsSignInUserButton,
                                    props?.resources?.checkoutButtonTitle,
                                    props?.isCheckoutBtnLoading!
                                )}
                        </div>
                        {/* {props.checkoutAsGuestButton} */}
                        {props.expressCheckoutButton && ArrayExtensions.hasElements(props.expressCheckoutButton) ? (
                            <Node {...props.ExpressCheckoutSectionWrapper}>{props.expressCheckoutButton}</Node>
                        ) : null}
                        {/* {props.backToShoppingButton}
                    {props.createTemplateFromCartButton} */}
                    </div>
                </Node>
            )}
            {props.storeSelector}
        </div>
    );
};

export default CartView;
